import React from 'react';
import classNames from 'classnames';

import Data, { _F } from 'data';
import Dialog from 'ui/dialog';

import { getIconPath } from 'common';
import Store from 'store';
import EditingContext from 'store/editingContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import Tooltip from 'ui/tooltip';
import { ItemIcon, WithItemTooltip, WithSkillTooltip, WithTripodTooltip } from 'tooltip';

import 'components/EngravingView.scss';
import { RunePicker, skillOrder } from 'components/editor/SkillEditor';
import { SkillSlot } from 'components/SkillView';
import Popup from 'ui/popup';
import NumberInput from 'ui/number';
import BlockClearButton, { builderHasSkills, builderSelectSkill, builderTripodLevel } from './common';

function EmptySkillSlot({ onClick, active }) {
  return (
    <div className={classNames("lap-SkillSlot lap-builder", {"lap-delta lap-delta-change": active})} onClick={onClick}>
      <div className="lap-skill-icon lap-add"/>
      <div className="lap-skill-name">Select skill...</div>
    </div>
  );
}

function SkillDisplay({ skill, pvp, disabled, onClick }) {
  const data = Data.skills[skill.id];
  if (!data) return null;
  return (
    <WithSkillTooltip skill={skill} pvp={pvp}>
      <div className={classNames("lap-skill-entry", {"lap-disabled": disabled})} onClick={disabled ? null : onClick}>
        <div className="lap-skill-icon" style={{backgroundImage: `url(${getIconPath(data.icon)}`}}/>
        <div className="lap-skill-name">{data.name}</div>
      </div>
    </WithSkillTooltip>
  );
}

const BuilderSkillPicker = ({ skills, onSelect, pvp }) => (
  <div className="lap-SkillPicker">
    {skills.map(({id, disabled}) => <SkillDisplay key={id} skill={{id}} pvp={pvp} disabled={disabled} onClick={() => onSelect(id)}/>)}
  </div>
);

const alignMethod = [{from: "tl", to: "bl"}, {from: "bl", to: "tl"}];

const tripodRows = [[1, 2, 3], [4, 5, 6], [7, 8]];

function BuilderSkillTripod({ id, index, pvp, locked }) {
  const skill = Store.useBuilder(builder => builder.skills?.[id]);
  const category = Store.useBuilder(builder => builder.category);
  const level = Store.useBuilder(builder => builderTripodLevel(builder, id, index));
  const dispatch = Store.useDispatch();
  const data = Data.skills[id]?.tripods?.[index - 1];
  const onClick = React.useCallback(() => dispatch.setBuilder(builder => {
    builder = {...builder, skills: {...builder.skills}};
    const skill = builder.skills[id] = {...builder.skills[id]};
    skill.tripods = {...skill.tripods};
    const row = tripodRows.findIndex(row => row.includes(index));
    const rowLevel = 4 + 3 * row;
    if (skill.tripods[index] && skill.level >= 4 + 3 * row) {
      delete skill.tripods[index];
      const reqLevel = Math.max(1, ...Object.keys(skill.tripods).map(index => {
        index = parseInt(index);
        return 4 + 3 * tripodRows.findIndex(row => row.includes(index));
      }));
      if (reqLevel < rowLevel && skill.level >= rowLevel) {
        skill.level = reqLevel;
      }
    } else {
      const value = Math.max(...tripodRows[row].map(index => skill.tripods[index] || 0));
      for (const index of tripodRows[row]) delete skill.tripods[index];
      skill.tripods[index] = Math.min(data.levels, value || 1);
      if (skill.level < rowLevel) {
        skill.level = rowLevel;
      }
    }
    return builder;
  }), [dispatch, id, index, data.levels]);
  const canLevelTripod = Store.useBuilder(builder => {
    if (!builder.skills) return true;
    if (!builder.skills[id]?.tripods?.[index] || builder.skills[id].tripods[index] > 1) return true;
    let count = 0;
    for (const skill of Object.values(builder.skills)) {
      if (!skill || !skill.tripods || !skill.level) continue;
      const row = Math.floor((skill.level - 4) / 3);
      for (let index = 1; index <= row * 3 + 3; ++index) {
        if (skill.tripods[index] > 1) count += 1;
      }
    }
    return count < 18;
  }, [id, index]);
  const onChangeLevel = delta => dispatch.setBuilder(builder => {
    if (!canLevelTripod) return builder;
    builder = {...builder, skills: {...builder.skills}};
    const skill = builder.skills[id] = {...builder.skills[id]};
    skill.tripods = {...skill.tripods};
    skill.tripods[index] = Math.max(1, Math.min(data.levels, (skill.tripods[index] || 1) + delta * (category === 'inferno' ? 5 : 1)));
    return builder;
  });
  if (!skill || !data) return null;
  let nextLevel = (level || 1) + 1;
  if (category === 'arena') nextLevel = null;
  if (category === 'inferno') nextLevel = 5;
  return (
    <WithTripodTooltip skill={id} tripod={index} level={level || 1} level2={nextLevel} pvp={pvp}>
      <div className={classNames("lap-tripod", {"lap-active": level > 0})}>
        <div className="lap-tripod-icon" style={{backgroundImage: `url(${getIconPath(data.icon)})`}} onClick={locked ? null : onClick}/>
        <div className="lap-name">{data.name}</div>
        {!!(level && data.levels > 1 && category !== 'arena') && (
          <div className="lap-level">
            {!locked && !!canLevelTripod && (
              <span className={classNames("lap-button", {"lap-hidden": level <= 1})} onClick={() => onChangeLevel(-1)}>-</span>
            )}
            <Tooltip tooltip={"No more than 18 tripods can be leveled up"} disabled={(canLevelTripod) || locked}>
              <span className="lap-text">{_F('sys.skillbook.tripod_level')} {level}</span>
            </Tooltip>
            {!locked && !!canLevelTripod && (
              <span className={classNames("lap-button", {"lap-hidden": level >= data.levels})} onClick={() => onChangeLevel(1)}>+</span>
            )}
          </div>
        )}
      </div>
    </WithTripodTooltip>
  );
}

function SkillRune({ id }) {
  const [expanded, setExpanded] = React.useState(false);
  const rune = Store.useBuilder(builder => builder.skills?.[id]?.rune);
  const dispatch = Store.useDispatch();
  const data = Data.items[rune];

  const numRunes = Store.useBuilder(builder => builder.skills ? Object.values(builder.skills).reduce((sum, skill) => sum + (skill.rune ? 1 : 0), 0) : 0);

  const nodeRef = React.useRef();

  const onToggle = React.useCallback(e => {
    const rect = nodeRef.current.getBoundingClientRect();
    setExpanded(e => e ? null : rect);
  }, [numRunes]);
  const onClose = React.useCallback(() => setExpanded(null), []);
  const onSelect = React.useCallback(value => {
    setExpanded(null);
    dispatch.setBuilder(builder => {
      builder = {...builder, skills: {...builder.skills}};
      builder.skills[id] = {...builder.skills[id], rune: value};
      return builder;
    });
  }, [dispatch, id]);
  const onClear = React.useCallback(e => {
    e.preventDefault();
    dispatch.setBuilder(builder => {
      builder = {...builder, skills: {...builder.skills}};
      builder.skills[id] = {...builder.skills[id], rune: null};
      return builder;
    });
  }, [dispatch, id]);

  return <>
    {data ? (
      <WithItemTooltip item={rune}>
        <div ref={nodeRef} onClick={onToggle} onContextMenu={onClear} className="lap-rune-picker">
          <div className={classNames(`lap-skill-rune lap-item-icon lap-item-background lap-item-${Data.itemGrades[data.grade || 0].color} lap-click`, {"lap-expanded": expanded})}>
            <ItemIcon item={data}/>
          </div>
          <span className={`la-color-${Data.itemGrades[data.grade || 0].color}`}>
            {data.name}
          </span>
        </div>
      </WithItemTooltip>
    ) : (
      <Tooltip disabled={numRunes < 8} tooltip={_F('sys.skillrune.alert_cannot_equip_over_max_count', 8)}>
        <div ref={nodeRef} onClick={numRunes >= 8 ? null : onToggle} className="lap-rune-picker">
          <div className={classNames("lap-skill-rune lap-empty lap-click", {"lap-expanded": expanded})}/>
          No Rune
        </div>
      </Tooltip>
    )}
    {!!expanded && (
      <Popup alignPosition={expanded} onClose={onClose}>
        <RunePicker id={id} onSelect={onSelect}/>
      </Popup>
    )}
  </>;
}

function BuilderSkillEditor({ id, charClass, pvp, tab, onChange, locked }) {
  const [selecting, setSelecting] = React.useState(false);
  const skills = Store.useBuilder(builder => builder.skills);
  const skill = Store.useBuilder(builder => builderSelectSkill(builder, id));
  const category = Store.useBuilder(builder => builder.category);

  const headerRef = React.useRef();
  const onOpenPicker = React.useCallback(() => setSelecting(selecting => {
    if (selecting) return null;
    let order = skillOrder(charClass).filter(id => !Data.skills[id].awakening);
    const tabInfo = Data.skillTabs[charClass]?.[tab];
    if (tabInfo?.types) {
      order = order.filter(id => tabInfo.types.includes(Data.skills[id].category));
    }
    return {
      skills: order.map(oid => ({id: oid, disabled: !!(id !== oid && skills?.[oid])})),
      position: headerRef.current.getBoundingClientRect(),
    };
  }), [charClass, tab, id, skills]);
  const onClosePicker = React.useCallback(() => setSelecting(null), []);

  const editingCallback = React.useCallback(({type, id: refId}) => {
    if (type === 'builderSkill' && id === refId) {
      onOpenPicker();
      return true;
    }
  }, [id, onOpenPicker]);
  EditingContext.useHandler(editingCallback);

  const maxLevel = Store.useBuilder(builder => {
    let maxPoints = 0, maxLevel = 12;
    if (builder.category === 'arena') {
      maxPoints = 340;
      maxLevel = 10;
    } else if (builder.category === 'inferno') {
      maxPoints = 404;
    } else {
      return 12;
    }
    if (!builder.skills) return maxLevel;
    for (const [oid, skill] of Object.entries(builder.skills)) {
      if (!skill || parseInt(oid) === parseInt(id)) continue;
      maxPoints -= Data.skillPoints[Math.min(maxLevel, skill.level || 0)] || 0;
    }
    while (maxLevel > 0 && maxPoints < Data.skillPoints[maxLevel]) {
      maxLevel -= 1;
    }
    return maxLevel;
  }, [id]);


  const dispatch = Store.useDispatch();
  const onSelect = React.useCallback(newId => {
    dispatch.setBuilder(builder => {
      builder = {...builder};
      builder.skills = {...builder.skills};
      const level = builder.skills[id]?.level || 1;
      delete builder.skills[id];
      if (newId) builder.skills[newId] = {level};
      return builder;
    });
    onChange(newId || 0);
    setSelecting(null);
  }, [dispatch, id, onChange]);
  const onDeselect = React.useCallback(e => {
    e.stopPropagation();
    onSelect(null);
  }, [onSelect]);
  const onSetLevel = React.useCallback(value => dispatch.setBuilder(builder => {
    builder = {...builder, skills: {...builder.skills}};
    const prevLevel = builder.skills[id]?.level || 1;
    const dst = builder.skills[id] = {...builder.skills[id], level: value};
    if (value > prevLevel && dst.tripods) {
      const prevRow = Math.floor((prevLevel - 4) / 3), newRow = Math.floor((value - 4) / 3);
      for (let index = prevRow * 3 + 4; index <= newRow * 3 + 3; ++index) {
        if (dst.tripods[index]) {
          dst.tripods = {...dst.tripods, [index]: 1};
        }
      }
    }
    return builder;
  }), [dispatch, id]);

  const data = Data.skills[id];

  return <>
    <div className="lap-skill-header" onClick={locked ? null : onOpenPicker} ref={headerRef}>
      {data ? <SkillDisplay skill={skill} pvp={pvp}/> : <div className="lap-skill-header-empty">Select skill...</div>}
      {!!id && !locked && <FontAwesomeIcon icon={faTimes} className="lap-skill-remove" onClick={onDeselect}/>}
    </div>
    {!!data && (
      <div className="lap-skill-settings">
        {data.maxLevel > 1 && <label>Level <NumberInput min={1} max={maxLevel} value={skill.level || 1} onChange={onSetLevel}/></label>}
        {!data.awakening && category !== 'arena' && <SkillRune id={id}/>}
      </div>
    )}
    {!!data?.tripods && tripodRows.map((indices, row) => (
      <div className={`lap-skill-tripod-row lap-row-${row + 1}`} key={row}>
        <h3 className={classNames({"lap-active": skill?.level >= 4 + 3 * row})}>{'III'.substring(0, row + 1)}</h3>
        <div className="lap-tripod-list">
          {indices.map(index => <BuilderSkillTripod id={id} index={index} key={index} pvp={pvp}/>)}
        </div>
      </div>
    ))}
    {!!selecting && (
      <Popup alignPosition={selecting.position} alignMethod={alignMethod} alignPadding={0} onClose={onClosePicker}>
        <BuilderSkillPicker skills={selecting.skills} onSelect={onSelect}/>
      </Popup>
    )}
  </>;
}

function TripodPopup({ id, pvp }) {
  const skills = Store.useBuilder(builder => builder.skills);
  const skill = skills?.[id] || {};

  const data = Data.skills[id];

  return (
    <div className="lap-TripodPopup">
      <div>
        <SkillDisplay skill={{id, ...skill}} pvp={pvp}/>
      </div>
      {!!data?.tripods && tripodRows.map((indices, row) => (
        <div className={`lap-skill-tripod-row lap-row-${row + 1}`} key={row}>
          <h3 className={classNames({"lap-active": skill?.level >= 4 + 3 * row})}>{'III'.substring(0, row + 1)}</h3>
          <div className="lap-tripod-list">
            {indices.map(index => <BuilderSkillTripod id={id} index={index} key={index} pvp={pvp} locked/>)}
          </div>
        </div>
      ))}
    </div>
  );
}

function AwakeningSlot({ pvp, editing }) {
  const [selecting, setSelecting] = React.useState(false);

  const charClass = Store.useBuilder(builder => builder.class);
  const skills = Store.useBuilder(builder => builder.skills);

  const order = React.useMemo(() => skillOrder(charClass).filter(id => Data.skills[id].awakening), [charClass]);

  const awakening = order.find(id => skills?.[id]);

  const headerRef = React.useRef();
  const onOpenPicker = React.useCallback(() => setSelecting(selecting => {
    if (selecting) return null;
    return {
      skills: order.map(id => ({id})),
      position: headerRef.current.getBoundingClientRect(),
    };
  }), [order]);
  const onClosePicker = React.useCallback(() => setSelecting(null), []);

  const dispatch = Store.useDispatch();
  const onSelect = React.useCallback(newId => {
    dispatch.setBuilder(builder => {
      builder = {...builder};
      builder.skills = {...builder.skills};
      delete builder.skills[awakening];
      if (newId) builder.skills[newId] = {};
      return builder;
    });
    setSelecting(null);
  }, [dispatch, awakening]);
  const onDeselect = React.useCallback(e => {
    e.preventDefault();
    onSelect(null);
  }, [onSelect]);

  return <>
    {awakening ? (
      <SkillSlot skill={{id: awakening, level: 1}} pvp={pvp} onClick={onOpenPicker} ref={headerRef} onContextMenu={onDeselect}/>
    ) : (
      <div className="lap-SkillSlot lap-awakening" onClick={onOpenPicker} ref={headerRef}>
        <div className="lap-empty-awakening"/>
        <div className="lap-skill-awakening">Awakening</div>
      </div>
    )}
    {!!selecting && (
      <Popup alignPosition={selecting.position} alignMethod={alignMethod} alignPadding={0} onClose={onClosePicker}>
        <BuilderSkillPicker skills={selecting.skills} onSelect={onSelect}/>
      </Popup>
    )}
  </>;
}

const defaultSkills = {};

export function useBuilderSkillPoints() {
  return Store.useBuilder(builder => {
    if (!builder.skills) return 0;
    return Object.values(builder.skills).reduce((sum, skill) => {
      if (!skill) return sum;
      return sum + (Data.skillPoints[Math.min(builder.category === 'arena' ? 10 : 12, skill.level || 0)] || 0);
    }, 0);
  });
}

function BuilderSkillSlot({ id, ...props }) {
  const skill = Store.useBuilder(builder => builderSelectSkill(builder, id));
  return <SkillSlot skill={skill} {...props} builder/>;
}


export default function BuilderSkills() {
  const [editId, setEdit] = React.useState(null);
  const [editPos, setEditPos] = React.useState(null);
  const [tab1, setTab] = React.useState();
  const [tripodPos, setTripodPos] = React.useState(false);

  const editing = EditingContext.useStateView("builderEditing");

  const builder = Store.useBuilder();

  const points = useBuilderSkillPoints();

  const skills = builder.skills || defaultSkills;

  const order = React.useMemo(() => skillOrder(builder.class), [builder.class]);

  const tab = React.useMemo(() => {
    const tabs = Data.skillTabs[builder.class];
    if (!tabs) return -1;
    return tabs[tab1] ? tab1 : 0;
  }, [tab1, builder.class]);

  const tabInfo = Data.skillTabs[builder.class]?.[tab];
  const tabLimit = tabInfo?.limit == null ? 8 : tabInfo.limit;

  const displayed = React.useMemo(() => order.filter(id => {
    if (tabInfo?.types && !tabInfo.types.includes(Data.skills[id].category)) return false;
    if (Data.skills[id].awakening) return false;
    return tabLimit === 0 || skills[id];
  }), [order, skills, tabInfo, tabLimit]);

  React.useEffect(() => {
    if (editId != null && (editId !== 0 || !tabLimit) && !displayed.includes(editId)) {
      setEdit(displayed[0] || 0);
    }
  }, [tabLimit, displayed, editId]);

  const pvp = builder.category === 'arena' || builder.category === 'gvg';

  const onClear = React.useCallback(builder => ({...builder, skills: {}}), []);

  const editDispatch = EditingContext.useDispatch();
  const onEditEmpty = React.useCallback(() => {
    setEdit(0);
    editDispatch({type: 'builderSkill', id: 0});
  }, [editDispatch]);
  const onSetEdit = React.useCallback((e, id) => {
    if (editing || Data.skills[id]?.tripods) {
      setEdit(prev => prev === id ? null : id);
      const node = !editing && e.target.closest('.lap-SkillSlot');
      if (node) {
        setEditPos(node.getBoundingClientRect());
      }
    }
  }, [editing]);
  const onClosePopup = React.useCallback(() => {
    setEdit(null);
    setEditPos(null);
  }, []);

  return <>
    <div className="lap-BuilderBlock lap-BuilderSkills">
      <div className="lap-BuilderBlockHeader">
        Skills
        {!!points && ` (${points} points)`}
        {!!(editing && builderHasSkills(builder)) && <BlockClearButton onClear={onClear}/>}
        {!editing && (
          <Tooltip tooltip="Show tripod positions">
            <div className={classNames("lap-tripod-toggle", {"lap-active": tripodPos})} onClick={() => setTripodPos(s => !s)}/>
          </Tooltip>
        )}
      </div>
      <div className={classNames("lap-SkillView", {"lap-editing": editing})}>
        {!!Data.skillTabs[builder.class] && (
          <div className="lap-tabs">
            {Data.skillTabs[builder.class].map((info, idx) => (
              <div key={idx} className={classNames({"lap-active": idx === tab})} onClick={() => setTab(idx)}>
                {info.name}
              </div>
            ))}
          </div>
        )}
        <div className="lap-skills">
          {displayed.map(id => <BuilderSkillSlot key={id} id={id} pvp={pvp} onClick={e => onSetEdit(e, id)} delta={editId === id && 'change'} showPosition={tripodPos}/>)}
          {!!(tabLimit && displayed.length < tabLimit && editing) && <EmptySkillSlot onClick={onEditEmpty} active={editId === 0}/>}
        </div>
        <div>
          <AwakeningSlot pvp={pvp} editing={editing}/>
        </div>
      </div>
    </div>
    {!!editing && !!((editId === 0 && tabLimit) || displayed.includes(editId)) && (
      <Dialog className="lap-BuilderEditor lap-builder-skills" onClose={() => setEdit(null)} title={`Editing skill`}>
        <Dialog.Content>
          <BuilderSkillEditor id={editId} pvp={pvp} charClass={builder.class} tab={tab} locked={!tabLimit} onChange={setEdit}/>
        </Dialog.Content>
      </Dialog>
    )}
    {!!(!editing && editId && editPos) && (
      <Popup alignPosition={editPos} onClose={onClosePopup}>
        <TripodPopup id={editId} pvp={pvp}/>
      </Popup>
    )}
  </>;
}
